import { getErrorMessage } from "@/utils/error";
import { toast } from "@givenwell/components";
import {
  ActionSupporterSurplusBody,
  ArchiveSupporterBody,
  CreateSupporterBody,
  CreateSupporterMemberBody,
  CreateSupporterMembersBody,
  CreateSupporterMembersIndividualResponse,
  CreateSupporterMembersResponse,
  InviteSupporterStaffBody,
  OpenAPI,
  SendMemberInvitationsBody,
  SetSupporterWalletBuffersBody,
  SupporterTransactionsService,
  UpdateSupporterAllowanceSettingsBody,
  UpdateSupporterBillingDetailsBody,
  UpdateSupporterBody,
  UpdateSupporterMemberBody,
  UpdateSupporterNoteBody,
} from "@givenwell/management-api";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { chunkMembersForRequest, PartialBulkCreateError } from "./chunkMembers";
import { api } from "./utils";

export const supporterQuery = (supporterId: string | undefined) => ({
  queryKey: ["supporters", supporterId],
  queryFn: () => api.supporter.getSupporter({ supporterId: supporterId! }),
  enabled: !!supporterId,
});

export const supportersSearchQueryKey = ["supporters-search"];

export const searchSupportersQuery = (searchTerm: string, page = 1, pageSize = 10, country?: string) =>
  queryOptions({
    queryKey: [...supportersSearchQueryKey, { searchTerm: searchTerm, page, pageSize, country }],
    queryFn: async () => {
      const response = await api.supporter.searchSupporters({ page, pageSize, query: searchTerm, country });
      return {
        query: searchTerm,
        items: response.items,
        total_items: response.total_items,
      };
    },
    placeholderData: previousData => previousData,
  });

export const membersQueryKey = (supporterId: string) => ["supporters", supporterId, "members"];

export const membersQuery = (supporterId: string, includeDeleted: boolean = false) =>
  queryOptions({
    queryKey: [...membersQueryKey(supporterId), { includeDeleted }],
    queryFn: async () => await api.supporter.getMembers({ supporterId, includeDeleted }),
    enabled: !!supporterId,
  });

export const membersPagedQueryKey = (supporterId: string) => ["supporters", supporterId, "members-paged"];

export const membersPagedQuery = (
  supporterId: string,
  page: number,
  pageSize: number,
  includeDeleted: boolean = false,
  sort?: string,
  query?: string,
) =>
  queryOptions({
    queryKey: [...membersPagedQueryKey(supporterId), { page, pageSize, includeDeleted, sort, query }],
    queryFn: async () =>
      await api.supporter.getMembersPaged({ supporterId, page, pageSize, includeDeleted, sort, query }),
    enabled: !!supporterId,
    placeholderData: previousData => previousData,
  });

export const supporterWalletsQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporters", supporterId, "wallets"],
    queryFn: async () => await api.supporter.getSupporterWallets({ supporterId }),
  });

export const supporterCountriesQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporters", supporterId, "countries"],
    queryFn: async () => await api.supporterCountries.getSupporterCountries({ supporterId }),
    enabled: !!supporterId,
  });

export function supporterNoteQuery(supporterId: string | undefined) {
  return queryOptions({
    queryKey: ["supporters", supporterId, "note"],
    queryFn: async () => await api.supporter.getSupporterNote({ supporterId: supporterId! }),
    enabled: !!supporterId,
  });
}

// -------------------------------------------------------------------------------------------------

export function useUpdateSupporterNoteMutation(supporterId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateSupporterNoteBody) => {
      await api.supporter.updateSupporterNote({ supporterId, requestBody: body });
    },
    onSuccess: () => client.invalidateQueries(supporterNoteQuery(supporterId)),
  });
}

export function useUpdateSupporterWalletsMutation(supporterId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (body: SetSupporterWalletBuffersBody) => {
      await api.supporter.setSupporterWalletBuffers({ supporterId, requestBody: body });
    },
    onError: error => toast.error("Failed to update buffers", { description: getErrorMessage(error) }),
    onSuccess: () => {
      client.invalidateQueries(supporterWalletsQuery(supporterId));
    },
  });
}

export function useUpdateSupporterMutation(supporterId: string) {
  return useMutation({
    mutationFn: async (body: UpdateSupporterBody) => {
      await api.supporter.updateSupporter({ supporterId, requestBody: body });
    },
  });
}

export function useSendInvitationsMutation() {
  return useMutation({
    mutationFn: async ({
      supporterId,
      supporter_member_ids,
      send_to_all,
    }: SendMemberInvitationsBody & { supporterId: string }) => {
      await api.supporter.sendMemberInvitations({
        supporterId,
        requestBody: { supporter_member_ids, send_to_all },
      });
    },
    onError: error => {
      toast.error("Failed to send invitations", {
        description: getErrorMessage(error),
      });
    },
  });
}

export function useSendPasswordResetMutation() {
  return useMutation({
    mutationFn: async ({ supporterId, supporterMemberId }: { supporterId: string; supporterMemberId: string }) => {
      await api.supporter.sendPasswordReset({
        supporterId,
        supporterMemberId,
      });
    },
    onError: error => {
      toast.error("Failed to send passwordreset", {
        description: getErrorMessage(error),
      });
    },
  });
}

export function useCreateSupporterMemberMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { supporterId: string; request: CreateSupporterMemberBody }) => {
      return await api.supporter.createSupporterMember({ supporterId: args.supporterId, requestBody: args.request });
    },
  });
}

export function useUpdateSupporterMemberMutation() {
  return useMutation({
    mutationFn: async ({
      supporterId,
      supporterMemberId,
      ...body
    }: { supporterId: string; supporterMemberId: string } & UpdateSupporterMemberBody) => {
      return await api.supporter.updateSupporterMember({ supporterId, supporterMemberId, requestBody: body });
    },
  });
}

export function useCreateSupporterMutation() {
  return useMutation({
    mutationFn: async (body: CreateSupporterBody) => api.supporter.createSupporter({ requestBody: body }),
  });
}

export const supporterAllowanceSettingsQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporter-allowance-settings", supporterId],
    queryFn: async () => {
      return await api.supporter.getSupporterAllowanceSettings({ supporterId });
    },
  });

export function useUpdateSupporterAllowanceSettingsMutation() {
  return useMutation({
    mutationFn: async ({ supporter_id, ...body }: UpdateSupporterAllowanceSettingsBody & { supporter_id: string }) => {
      return await api.supporter.updateSupporterAllowanceSettings({ supporterId: supporter_id, requestBody: body });
    },
  });
}

export const supporterBillingDetailsQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporter-billing-details", supporterId],
    queryFn: async () => {
      return await api.supporter.getSupporterBillingDetails({ supporterId });
    },
  });

export function useUpdateSupporterBillingDetailsMutation() {
  return useMutation({
    mutationFn: async (args: { supporterId: string; body: UpdateSupporterBillingDetailsBody }) => {
      return await api.supporter.updateSupporterBillingDetails({
        supporterId: args.supporterId,
        requestBody: args.body,
      });
    },
  });
}

export type TokenCurrency = Parameters<
  typeof SupporterTransactionsService.getTransactionsForSupporter
>[0]["tokenCurrency"];

export const supporterWalletTransactionsQuery = (
  supporterId: string,
  tokenCurrency: TokenCurrency,
  page: number,
  pageSize: number,
) =>
  queryOptions({
    queryKey: ["supporter-wallet-transactions", supporterId, tokenCurrency, page, pageSize],
    queryFn: async () => {
      return await api.supporterTransactions.getTransactionsForSupporter({
        supporterId,
        tokenCurrency,
        pageNum: page,
        pageSize,
      });
    },
  });

export const supporterWalletVersionTransactionsQuery = (supporterId: string, version: number) =>
  queryOptions({
    queryKey: ["supporter-wallet-version-transactions", supporterId, version],
    queryFn: async () => {
      return await api.supporterTransactions.getTransactionsForSupporterVersion({ supporterId, version });
    },
  });

export function useCreateSupporterMembersMutation(
  onSuccess: () => void,
  onError?: (error: any) => void,
  // Called after each chunk so the caller can show how far through the upload we are.
  onProgress?: (created: number, total: number) => void,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ supporterId, members, ...rest }: { supporterId: string } & CreateSupporterMembersBody) => {
      // Split into several requests so each body stays under the AWS WAF's 8KB limit. Sent one at a
      // time because the API rejects an entire chunk if any of its email addresses already exist,
      // and we need to know exactly which members made it in before that happened.
      const chunks = chunkMembersForRequest(members, rest);
      const created: CreateSupporterMembersIndividualResponse[] = [];
      onProgress?.(0, members.length);

      for (const chunk of chunks) {
        try {
          const response = await api.supporter.createMembers({
            supporterId,
            requestBody: { ...rest, members: chunk },
          });
          created.push(...response.supporter_members);
          onProgress?.(created.length, members.length);
        } catch (error) {
          if (created.length === 0) throw error;
          throw new PartialBulkCreateError(
            error,
            created.map(m => m.email),
          );
        }
      }

      return { supporter_members: created } satisfies CreateSupporterMembersResponse;
    },
    onSettled: (_, __, variables) => {
      client.invalidateQueries({ queryKey: membersQueryKey(variables.supporterId) });
      client.invalidateQueries({ queryKey: membersPagedQueryKey(variables.supporterId) });
    },
    onSuccess,
    onError,
  });
}

type RemoveSupporterMemberMutationParams = {
  supporterId: string;
  supporterMemberId: string;
  /** Email the member that their access has ended. Off by default. */
  notifyMember: boolean;
};
/** Removes the member right now. The server archives any active allowance rules first. */
export function useRemoveSupporterMemberMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ supporterId, supporterMemberId, notifyMember }: RemoveSupporterMemberMutationParams) => {
      await api.supporter.removeSupporterMember({ supporterId, supporterMemberId, notifyMember });
    },
    onError(error) {
      toast.error(getErrorMessage(error));
    },
    onSettled: (_, __, variables) => {
      client.invalidateQueries({ queryKey: membersQueryKey(variables.supporterId) });
      client.invalidateQueries({ queryKey: membersPagedQueryKey(variables.supporterId) });
    },
  });
}

type ScheduleMemberRemovalMutationParams = {
  supporterId: string;
  supporterMemberId: string;
  /** yyyy-mm-dd in the supporter's local calendar; must be after today in the supporter's time zone. */
  date: string;
  notifyMember: boolean;
};
/** Schedules (or re-dates) the member's removal for 5pm on the given date in the supporter's time zone. */
export function useScheduleMemberRemovalMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ supporterId, supporterMemberId, date, notifyMember }: ScheduleMemberRemovalMutationParams) => {
      return await api.supporter.scheduleSupporterMemberRemoval({
        supporterId,
        supporterMemberId,
        requestBody: { date, notify_member: notifyMember },
      });
    },
    onError(error) {
      toast.error(getErrorMessage(error));
    },
    onSettled: (_, __, variables) => {
      client.invalidateQueries({ queryKey: membersQueryKey(variables.supporterId) });
      client.invalidateQueries({ queryKey: membersPagedQueryKey(variables.supporterId) });
    },
  });
}

export function useCancelMemberRemovalMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ supporterId, supporterMemberId }: { supporterId: string; supporterMemberId: string }) => {
      await api.supporter.cancelSupporterMemberRemoval({ supporterId, supporterMemberId });
    },
    onError(error) {
      toast.error(getErrorMessage(error));
    },
    onSettled: (_, __, variables) => {
      client.invalidateQueries({ queryKey: membersQueryKey(variables.supporterId) });
      client.invalidateQueries({ queryKey: membersPagedQueryKey(variables.supporterId) });
    },
  });
}

type SurplusStatus = Parameters<typeof api.supporter.getSupporterSurpluses>[0]["status"];

export const surplusesQueryKey = ["surpluses"];

export const surplusesQuery = (page = 1, pageSize = 10, status: SurplusStatus) =>
  queryOptions({
    queryKey: [...surplusesQueryKey, status, page, pageSize],
    queryFn: async () => {
      return await api.supporter.getSupporterSurpluses({ page, pageSize, status });
    },
  });

export type SurplusActions = ActionSupporterSurplusBody["action"];

export function useActionSupporterSurplusMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { surplusIds: string[]; supporterId: string | null; action: SurplusActions }) => {
      await api.supporter.actionSupporterSurplus({
        requestBody: {
          surplus_ids: args.surplusIds,
          action: args.action,
          supporter_id: args.supporterId,
        },
      });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: surplusesQueryKey });
    },
  });
}

export const supporterStaffQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporters", supporterId, "staff"],
    queryFn: () => api.supporter.getSupporterStaff({ supporterId }),
    select: response => response.staff_users,
  });

export function useInviteSupporterStaffMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (args: { supporterId: string; request: InviteSupporterStaffBody }) => {
      return await api.supporter.inviteSupporterStaff({ supporterId: args.supporterId, requestBody: args.request });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(supporterStaffQuery(variables.supporterId));
    },
  });
}

export function useDeleteSupporterStaffMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { supporterId: string; userId: string }) => {
      await api.supporter.deleteSupporterStaff({ supporterId: args.supporterId, userId: args.userId });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(supporterStaffQuery(variables.supporterId));
    },
  });
}

export function useResendSupporterStaffInvitationMutation() {
  return useMutation({
    mutationFn: async ({ supporterId, invitationId }: { supporterId: string; invitationId: string }) => {
      await api.supporter.resendSupporterStaffInvitation({ supporterId, invitationId });
    },
  });
}

export function useArchiveSupporterMutation() {
  const client = useQueryClient();

  return useMutation({
    // `body` is omitted entirely for the ordinary case, which keeps the request a bodiless
    // DELETE exactly as before. It is only supplied to opt into cancelling leftover float.
    mutationFn: async (args: { supporterId: string; body?: ArchiveSupporterBody }) => {
      return await api.supporter.archiveSupporter({
        supporterId: args.supporterId,
        requestBody: args.body,
      });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(supporterQuery(variables.supporterId));
      // Archiving can cancel the wallet float and closes the accounts, so the cached balances
      // are stale either way.
      client.invalidateQueries(supporterWalletsQuery(variables.supporterId));
      setTimeout(() => {
        // Allow time for the search index to update.
        client.invalidateQueries({ queryKey: supportersSearchQueryKey });
      }, 2000);
    },
  });
}

// -------------------------------------------------------------------------------------------------
// Member Fee Discounts
// -------------------------------------------------------------------------------------------------

export const supporterPlansQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporters", supporterId, "plans"],
    queryFn: async () => await api.supporter.getPlans({ supporterId }),
  });

/** The plans a new supporter in a country could be created on, priced in that country's currency. */
export const plansForCountryQuery = (countryCode: string) =>
  queryOptions({
    queryKey: ["plans", countryCode],
    queryFn: async () => await api.supporter.getPlansForCountry({ country: countryCode }),
  });

export const memberFeeDiscountsQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporters", supporterId, "member-fee-discounts"],
    queryFn: async () => await api.supporter.getMemberFeeDiscounts({ supporterId }),
  });

export function useCreateMemberFeeDiscountMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      supporterId: string;
      startDate: string;
      endDate: string;
      memberFeeCents: number;
      baseFeeCents: number;
      note?: string;
    }) => {
      return await api.supporter.createMemberFeeDiscount({
        supporterId: args.supporterId,
        requestBody: {
          start_date: args.startDate,
          end_date: args.endDate,
          member_fee_cents: args.memberFeeCents,
          base_fee_cents: args.baseFeeCents,
          note: args.note,
        },
      });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(memberFeeDiscountsQuery(variables.supporterId));
    },
    onError: error => toast.error("Failed to create discount", { description: getErrorMessage(error) }),
  });
}

export function useUpdateMemberFeeDiscountMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      supporterId: string;
      discountId: string;
      startDate: string;
      endDate: string;
      memberFeeCents: number;
      baseFeeCents: number;
      note?: string;
    }) => {
      return await api.supporter.updateMemberFeeDiscount({
        supporterId: args.supporterId,
        discountId: args.discountId,
        requestBody: {
          start_date: args.startDate,
          end_date: args.endDate,
          member_fee_cents: args.memberFeeCents,
          base_fee_cents: args.baseFeeCents,
          note: args.note,
        },
      });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(memberFeeDiscountsQuery(variables.supporterId));
    },
    onError: error => toast.error("Failed to update discount", { description: getErrorMessage(error) }),
  });
}

export function useDeleteMemberFeeDiscountMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { supporterId: string; discountId: string }) => {
      await api.supporter.deleteMemberFeeDiscount({
        supporterId: args.supporterId,
        discountId: args.discountId,
      });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(memberFeeDiscountsQuery(variables.supporterId));
    },
    onError: error => toast.error("Failed to delete discount", { description: getErrorMessage(error) }),
  });
}

/**
 * Sets the supporter's plan and, optionally, overrides of its rates. Null fees clear the overrides
 * so the plan's rates apply.
 */
export function useUpdateSupporterPlanMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      supporterId: string;
      /** Null clears the override, so the plan's rate applies. */
      memberFeeCents: number | null;
      baseFeeCents: number | null;
      planId?: string;
      /** Required whenever either fee is overridden. */
      planOverrideReason?: string;
    }) => {
      await api.supporter.updateSupporterPlan({
        supporterId: args.supporterId,
        requestBody: {
          member_fee_cents: args.memberFeeCents,
          base_fee_cents: args.baseFeeCents,
          plan_id: args.planId,
          plan_override_reason: args.planOverrideReason,
        },
      });
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(supporterBillingDetailsQuery(variables.supporterId));
    },
    onError: error => toast.error("Failed to update platform fee", { description: getErrorMessage(error) }),
  });
}

// -------------------------------------------------------------------------------------------------
// Supporter Settings (JSONB-based per-supporter settings)
// -------------------------------------------------------------------------------------------------

async function fetchAuthenticated<T>(url: string, options?: RequestInit): Promise<T> {
  const token = typeof OpenAPI.TOKEN === "function" ? await OpenAPI.TOKEN({ method: "GET", url }) : OpenAPI.TOKEN;
  const response = await fetch(`${OpenAPI.BASE}${url}`, {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  if (response.status === 204 || response.headers.get("content-length") === "0") return {} as T;
  return response.json();
}

export type SupporterSettings = Record<string, unknown>;

export const supporterSettingsQuery = (supporterId: string) =>
  queryOptions({
    queryKey: ["supporters", supporterId, "settings"],
    queryFn: () => fetchAuthenticated<SupporterSettings>(`/management/supporter/${supporterId}/settings`),
  });

export const supporterSettingQuery = (supporterId: string, key: string) =>
  queryOptions({
    ...supporterSettingsQuery(supporterId),
    select: settings => settings[key],
  });

export function useSetSupporterSettingMutation(supporterId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      await fetchAuthenticated(`/management/supporter/${supporterId}/settings/${key}`, {
        method: "PUT",
        body: JSON.stringify(value),
      });
    },
    onError: error => toast.error("Failed to update setting", { description: getErrorMessage(error) }),
    onSuccess: () => {
      client.invalidateQueries(supporterSettingsQuery(supporterId));
    },
  });
}

export function useDeleteSupporterSettingMutation(supporterId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ key }: { key: string }) => {
      await fetchAuthenticated(`/management/supporter/${supporterId}/settings/${key}`, {
        method: "DELETE",
      });
    },
    onError: error => toast.error("Failed to remove setting", { description: getErrorMessage(error) }),
    onSuccess: () => {
      client.invalidateQueries(supporterSettingsQuery(supporterId));
    },
  });
}
